import { type FocusEvent, createContext } from 'react';

import type { FormControlState } from '../../core/types/form-control-state.js';
import type { ValidationState } from '../validation/types.js';

type RadioGroupContextType = {
  /** The name of the radio group. */
  name?: string;
  /**
   * Internal: associates the group's radios with a `_Form` rendered outside
   * their React subtree via the native `form` attribute. Unstable.
   * @internal
   */
  form?: string;
  /** Whether the radio group is invalid. */
  hasError?: boolean;
  /** The currently selected value. */
  value: string | undefined | null;
  /** Whether the radio group is disabled. */
  isDisabled?: boolean;
  /** Whether the radio group is aria-disabled. */
  ariaDisabled?: boolean;
  /** Whether the radio group is readOnly. */
  readOnly?: boolean;
  /**
   * Whether the radio group is required.
   * @defaultValue false
   * */
  required?: boolean;
  /** Handler that is called when the value of the group changes. */
  onChange?: (value: string, event?: FocusEvent<HTMLInputElement>) => void;
  /** Describes an enhanced representation of a control validity state and hints about its error for the end user. */
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- APPDEV-18321
  controlState?: FormControlState;
  /**
   * Called when validation state changes. Fires automatically on blur or change,
   * and when triggered manually via `validate()` or `validateAsync()`.
   */
  onValidityChange?: (state: ValidationState) => void;
};

/** Context describing the state of a RadioGroup */
export const RadioGroupContext = createContext<
  RadioGroupContextType | undefined
>(undefined);
